import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

// Reveals contact details by calling the real unlock-contact endpoint.
// If the application isn't locked, renders immediately with no click needed.
// children must be a render-prop: (phone, email) => ReactNode
export default function RevealContact({
  applicationId,
  locked,
  phone,
  email,
  onUnlocked,
  children,
  label = 'Click to view mobile number',
}) {
  const [revealed, setRevealed] = useState(!locked);
  const [revealedPhone, setRevealedPhone] = useState(phone);
  const [revealedEmail, setRevealedEmail] = useState(email);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const navigate = useNavigate();

  if (revealed) {
    return typeof children === 'function' ? children(revealedPhone, revealedEmail) : children;
  }

  const handleReveal = async (e) => {
    e.stopPropagation();
    if (loading || !applicationId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(`/employer/applications/${applicationId}/unlock-contact`);
      if (data.unlocked) {
        setRevealedPhone(data.phone);
        setRevealedEmail(data.email);
        setRevealed(true);
        onUnlocked && onUnlocked(applicationId, data.phone, data.email);
        window.dispatchEvent(new Event("applicant-contact-unlocked"));
      } else {
        setError('Could not unlock contact. Please try again.');
      }
    } catch (err) {
      const d = err?.response?.data;
      if (d?.upgrade_required) {
        setUpgradeInfo(d);
      } else {
        console.error('Failed to unlock contact', err);
        setError('Could not unlock contact. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="inline-flex flex-col gap-1">
      <button
        type="button"
        onClick={handleReveal}
        disabled={loading}
        className="inline-flex items-center gap-2 text-sm font-semibold text-violet-700 border border-violet-200 bg-violet-50 hover:bg-violet-100 disabled:opacity-60 rounded-lg px-3 py-1.5 transition"
      >
        {loading ? 'Unlocking…' : label}
      </button>
      {error && <span className="text-xs text-rose-600">{error}</span>}

      {upgradeInfo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setUpgradeInfo(null)}
        >
          <div
            className="relative w-full max-w-lg overflow-hidden rounded-2xl border-2 border-[#7C3AED] bg-gradient-to-br from-[#EFF6FF] via-[#F5F3FF] to-[#FFF7ED] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <span className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-[#2563EB] to-[#7C3AED] text-white text-2xl flex-shrink-0">
                &#128274;
              </span>
              <div className="flex-1">
                <p className="text-lg font-extrabold text-[#0D1B3D] mb-1">
                  You've used all {upgradeInfo.free_limit} free contact unlock{upgradeInfo.free_limit === 1 ? '' : 's'} for this job
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Use credits to unlock this contact right now
                  {upgradeInfo.credits_per_unlock ? ` (${upgradeInfo.credits_per_unlock} credit${upgradeInfo.credits_per_unlock === 1 ? '' : 's'}/unlock` : ''}
                  {upgradeInfo.credit_balance != null ? ` — you have ${upgradeInfo.credit_balance} credits)` : upgradeInfo.credits_per_unlock ? ')' : ''}, or upgrade your plan for more free unlocks per job.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/employer/settings/billing')}
                    className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white font-bold px-5 py-2.5 rounded-xl hover:shadow-lg transition"
                  >
                    Upgrade Plan
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/employer/settings/billing?tab=credits')}
                    className="border-2 border-[#7C3AED] text-[#7C3AED] font-bold px-5 py-2.5 rounded-xl hover:bg-white transition"
                  >
                    Buy Credits
                  </button>
                  <button
                    type="button"
                    onClick={() => setUpgradeInfo(null)}
                    className="text-sm text-gray-400 hover:text-gray-600 px-2 py-2.5"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
